<template>
  <div>
    <b-navbar id="header" type="light" variant="light" sticky>
      <b-navbar-nav>
        <h4 class="mb-0">{{title}}</h4>
      </b-navbar-nav>

      <b-navbar-nav class="ml-auto">
        <b-row>
          <b-col cols="auto">
            <b-button id="view-button" size="sm" variant="warning" @click="toggleView">
              <font-awesome-icon :icon="viewMode === 'table' ? 'th-large' : 'list'"/>
            </b-button>
            <b-tooltip target="view-button" triggers="hover" variant="warning" noninteractive>
              {{ viewMode === 'table' ? 'Канбан' : 'Таблица' }}
            </b-tooltip>
            <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields @click="onPopoverClose">
              <font-awesome-icon icon="cog"/>
            </b-button>
            <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
              Колонки
            </b-tooltip>
            <b-btn v-if="canEditRecruitTasks()" size="sm" variant="success" :disabled="isBusy" @click="showModal(null, false)">
              <b-spinner v-if="isBusy" small/>
              <font-awesome-icon icon="plus" class="mr-1"/>
              Новая
            </b-btn>
          </b-col>
        </b-row>
      </b-navbar-nav>
    </b-navbar>

    <filters-drawer
      v-if="!isIntegrated"
      :hide-search="true"
      :is-busy="isBusy"
      :filter-tags="activeFilterTags"
      @remove-tag="removeFilterTag"
      @submit="onSearch"
      @clear="onClearFilters"
    >
      <b-col cols="12">
        <label class="small text-muted">Вакансии</label>
        <vacancy-multi-select
          id="vacancy-multi-select"
          ref="vacancy_multi_select"
          @onSelect="onSelectVacancy"
        />
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Дата открытия</label>
        <b-row class="align-items-center">
          <b-col>
            <b-form-input
              id="input-start-opened-date"
              size="sm"
              type="date"
              v-model.date="filters.start_opened_date"
            />
          </b-col>
          <b-col cols="auto" class="px-2">
            <span>&mdash;</span>
          </b-col>
          <b-col>
            <b-form-input
              id="input-end-opened-date"
              size="sm"
              type="date"
              v-model.date="filters.end_opened_date"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Дата закрытия</label>
        <b-row class="align-items-center">
          <b-col>
            <b-form-input
              id="input-start-closed-date"
              size="sm"
              type="date"
              v-model.date="filters.start_closed_date"
            />
          </b-col>
          <b-col cols="auto" class="px-2">
            <span>&mdash;</span>
          </b-col>
          <b-col>
            <b-form-input
              id="input-end-closed-date"
              size="sm"
              type="date"
              v-model.date="filters.end_closed_date"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Объекты</label>
        <location-multi-select
          id="location-multi-select"
          ref="location_multi_select"
          @onSelect="onSelectLocation"
        />
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Участники</label>
        <user-multi-select
          id="user-multi-select"
          ref="user_multi_select"
          title="Участники"
          @onSelect="onSelectUser"
        />
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Ответственные</label>
        <user-multi-select
          id="user-multi-select-assigned"
          ref="user_multi_select_assigned"
          title="Ответственные"
          @onSelect="onSelectUserAssigned"
        />
      </b-col>
      <b-col cols="12" class="mt-1">
        <b-row class="align-items-center">
          <b-col cols="6">
            <label class="small text-muted">Статус</label>
            <b-form-select
              id="input-statuses"
              size="sm"
              v-model="filters.status"
              :options="statusOptions"
            />
          </b-col>
          <b-col cols="6">
            <label class="small text-muted">Срочно</label>
            <b-form-select
              id="input-urgency"
              size="sm"
              v-model="filters.urgency"
            >
              <option :value="null">Все заявки</option>
              <option :value="true">Да</option>
              <option :value="false">Нет</option>
            </b-form-select>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" class="mt-1">
        <label class="small text-muted">Теги</label>
        <recruit-tag-multi-select
          id="tag-multi-select"
          ref="tag_multi_select"
          @onSelect="onSelectTag"
        />
      </b-col>
    </filters-drawer>

    <b-table
      v-if="viewMode === 'table'"
      id="recruit-tasks"
      ref="recruit_tasks"
      class="mb-0 text-small"
      select-mode="multi"
      head-variant="light"
      :tbody-tr-class="tbodyRowClass"
      sticky-header
      striped
      small
      responsive
      show-empty
      :empty-text="$t('message.common.emptyTable')"
      :busy.sync="isBusy"
      :items="items"
      :fields="renderedFields"
      no-provider-filtering
      no-provider-paging
      no-provider-sorting
      no-local-sorting
      @sort-changed="sortChanged"
      @row-clicked="rowClicked"
      @row-dblclicked="onRowDblClicked"
    >
      <template #table-busy>
        <div class="text-center text-success my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong>Загрузка</strong>
        </div>
      </template>

      <template #head(selected)="row">
        <b-form-checkbox
          class="mx-1"
          v-model="allSelected"
          :indeterminate="indeterminate"
          :disabled = "allSelectDisabled"
          size="md"
          @change="selectAllRows"
        />
      </template>

      <template #cell(selected)="row">
        <template v-if="row.item.selected">
          <span aria-hidden="true">&check;</span>
          <span class="sr-only">Выбрана</span>
        </template>
        <template v-else>
          <span >&nbsp;</span>
          <span class="sr-only">Не выбрана</span>
        </template>
      </template>

      <template #cell(assigned)="row">
        {{ getAssignedName(row.item.assigned) }}
      </template>

      <template #cell(status)="row">
        {{ getStatusText(row.item.status) }}
      </template>

      <template #cell(opened_date)="row">
        {{formatTableDate(row.item.opened_date)}}
      </template>

      <template #cell(closed_date)="row">
        {{formatTableDate(row.item.closed_date)}}
      </template>

      <template #cell(urgency)="row">
        {{ row.item.urgency ? 'Да' : 'Нет' }}
      </template>

      <template #cell(tags)="row">
        <div v-if="row.value">
          <b-badge
            v-for="tag in row.value"
            :key="tag.id"
            pill
            :style="getBadgeStyle(tag.color)"
            class="mr-1 px-2 py-1"
          >
            {{ tag.name }}
          </b-badge>
        </div>
      </template>

      <template #cell(action)="row">
        <b-dropdown size="sm" no-caret>
          <template #button-content>
            <font-awesome-icon icon="ellipsis-v"/>
          </template>
          <b-dropdown-item-button variant="success" @click.stop="showModal(row.item.id, true)">
            <font-awesome-icon class="mr-1" icon="eye"/>
            Смотреть
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditRecruitTasks()" variant="primary" @click.stop="showModal(row.item.id, false)">
            <font-awesome-icon class="mr-1" icon="edit"/>
            Редактировать
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditRecruitJobPostings()" variant="info" @click.stop="handleJobPosting(row.item.id)">
            <font-awesome-icon class="mr-1" icon="search-plus"/>
            Создать объявление
          </b-dropdown-item-button>
          <b-dropdown-item-button v-if="canEditRecruitTasks()" variant="danger" @click.stop="onRemove(row)">
            <font-awesome-icon class="mr-1" icon="trash"/>
            Удалить
          </b-dropdown-item-button>
        </b-dropdown>
      </template>
    </b-table>

    <kanban
      v-if="viewMode === 'kanban'"
      :items="items"
      :columns="kanbanColumns"
      group-name="recruit-tasks"
      @status-updated="kanbanUpdateStatus"
    >
      <template #card="{ item }">
        <b-card
          no-body
          class="kb-card p-2 bg-white shadow-sm rounded"
          :class="(item.urgency) ? 'border-danger' : 'border-white'"
          @dblclick="onRowDblClicked(item)"
        >
          <b-row class="mb-2">
            <b-col cols="3" class="mr-auto">
              <strong class="text-primary">#{{ item.id }}</strong>
            </b-col>
            <b-col cols="9" class="d-flex align-items-center justify-content-end">
              <b-badge variant="light" class="border">{{ formatTableDate(item.opened_date) }}</b-badge>
              <span>&mdash;</span>
              <b-badge variant="light" class="border">{{ formatTableDate(item.closed_date) }}</b-badge>
            </b-col>
          </b-row>
          <b-row class="mb-2 font-weight-bold">
            <b-col>
              {{ item.location_vacancy_name }}
            </b-col>
          </b-row>
          <b-row class="small text-muted mb-2">
            <b-col>
              <font-awesome-icon icon="map-marked-alt" style="color: green" class="mr-1"/>
              {{ item.location_name }}
            </b-col>
          </b-row>
          <b-row class="small text-muted mb-2">
            <b-col>
              <font-awesome-icon icon="user" class="mr-1"/>
              {{ getAssignedName(item.assigned) || 'Не назначен' }}
            </b-col>
          </b-row>
          <hr class="my-1"/>
          <b-row>
            <b-col cols="10" class="d-flex flex-wrap align-items-center">
              <b-badge v-if="item.urgency" class="mr-2" variant="danger">Срочно</b-badge>

              <div v-if="item.tags" class="d-flex flex-wrap mt-1">
                <b-badge
                  v-for="tag in item.tags"
                  :key="tag.id"
                  pill
                  :style="getBadgeStyle(tag.color)"
                  class="mr-1 mb-1 px-2 py-1"
                  style="font-size: 10px;"
                >
                  {{ tag.name }}
                </b-badge>
              </div>
            </b-col>
            <b-col cols="2" class="d-flex justify-content-end">
              <b-button size="sm" variant="link" class="p-0" @click="onRowDblClicked(item)">
                Открыть
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </template>
    </kanban>

    <pagination-footer
      :total-rows="totalRows"
      :per-page.sync="perPage"
      @page-click="onPagination"
      @per-page-change="onPerPageChange"
    >
      <template #footer-update>
        <b-row v-if="selected.length > 0" align-h="between" align-v="center">
          <b-col cols="auto" class="mr-auto text-small">
            Выбрано: <b>{{selected.length}}</b>
          </b-col>
          <b-col cols="auto">
            <b-button id="updateStatusButton" size="sm" ref="updateStatusButton" @click="popoverShow = !popoverShow" variant="info">
              <font-awesome-icon icon="exchange-alt" class="mr-1"/>
              Изменить статус
            </b-button>
          </b-col>
        </b-row>
        <hr v-if="selected.length > 0"/>
      </template>
    </pagination-footer>

    <b-popover
      v-if="selected.length > 0"
      target="updateStatusButton"
      triggers="click"
      :show.sync="popoverShow"
      placement="auto"
      container="my-container"
      ref="popover_recruitTasks"
      @shown="onShown"
      @hidden="onHidden"
    >
      <template #title>
        <div class="d-flex justify-content-between align-items-center w-100">
          <small class="font-weight-bold text-muted">Назначить статус</small>
          <b-button @click="onPopoverClose" class="close pb-1" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </b-button>
        </div>
      </template>
      <b-form-select
        ref="allTasksStatusSelect"
        class="mb-2"
        size="sm"
        v-model="selectedNewStatus"
        :options="statusOptions"
      />
      <div class="d-flex justify-content-end">
        <b-button @click="onPopoverClose" size="sm" class="mr-1" variant="secondary">Закрыть</b-button>
        <b-button @click="onPopoverOk" size="sm" variant="success" :disabled="!selectedNewStatus || isBusy">Назначить</b-button>
      </div>
    </b-popover>

    <dynamic-tables
      storage-key="recruitTasks_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

    <recruit-task
      ref="recruit_task"
      :location-id="locationId"
      :location-name="locationName"
      :is-integrated="isIntegrated"
      @refresh="getItems"
      @modal-closed="onModalClosed"
    />

  </div>
</template>

<script>
import recruitTasks from "../../network/recruitTasks";
import catalogs from "../../network/catalogs";
import users from "../../network/users";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import Kanban from "../../utilites/commonUi/Kanban.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import draggable from 'vuedraggable';
    import RecruitTask from "./RecruitTask"
    import LocationMultiSelect from "../Locations/LocationMultiSelect.vue";
    import UserMultiSelect from "../Users/UserMultiSelect.vue";
    import VacancyMultiSelect from "../Catalogs/Vacancies/VacancyMultiSelect.vue";
    import RecruitTagMultiSelect from "../Catalogs/RecruitTags/RecruitTagMultiSelect.vue";

export default {
  name: "RecruitTasks",
  components: { RecruitTagMultiSelect, PaginationFooter, Kanban, FiltersDrawer, draggable, UserMultiSelect, LocationMultiSelect, RecruitTask, DynamicTables, VacancyMultiSelect },
  mixins: [
    recruitTasks, catalogs, users,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  props: {
    locationId: { type: Number, default: null },
    locationName: { type: String, default: null },
    isIntegrated: { type: Boolean, default: false },
    disabledRouting: { type: Boolean, default: false },
  },
  data() {
    return {
      title: 'Заявки на подбор',
      sortBy: 'id',
      sortDesc: true,
      items: [],
      usersList: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class:'recruitTasksSelect'
      },
      fields: [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          visible: true,
          class: 'recruitTasksId'
        },
        {
          key: 'location_vacancy_name',
          label: 'Вакансия',
          sortable: true,
          visible: true,
        },
        {
          key: 'location_name',
          label: 'Объект',
          sortable: true,
          visible: true,
        },
        {
          key: 'assigned',
          label: 'Ответственный',
          sortable: true,
          visible: true,
        },
        {
          key: 'status',
          label: 'Статус',
          sortable: true,
          visible: true,
        },
        {
          key: 'opened_date',
          label: 'Дата открытия',
          sortable: true,
          visible: true,
        },
        {
          key: 'closed_date',
          label: 'Дата закрытия',
          sortable: true,
          visible: true,
        },
        {
          key: 'urgency',
          label: 'Срочно',
          sortable: true,
          visible: true,
          class: 'text-center'
        },
        {
          key: 'tags',
          label: 'Теги',
          visible: true,
          sortable: false
        },
      ],
      serviceEndField: {
        key: 'action',
        label: 'Действия',
        class: 'recruitTasksAction',
      },
      isBusy: false,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        location_vacancy_name: '',
      },
      statusOptions: [
        { value: null, text: 'Все статусы' },
        { value: 'created', text: 'Создана' },
        { value: 'assigned', text: 'Назначен рекрутер' },
        { value: 'in_progress', text: 'В работе' },
        { value: 'done', text: 'Готова' },
        { value: 'canceled', text: 'Отменена' }
      ],
      viewMode: 'table',
      kanbanColumns: [
        { id: 'created', title: 'Создана', variant: 'secondary' },
        { id: 'assigned', title: 'Назначен рекрутер', variant: 'info' },
        { id: 'in_progress', title: 'В работе', variant: 'primary' },
        { id: 'done', title: 'Готова', variant: 'success' },
        { id: 'canceled', title: 'Отменена', variant: 'danger' }
      ],
      popoverShow: false,
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: true,
      selectedNewStatus: null,
      disableRouting: this.disabledRouting,
    }
  },
  async mounted() {
    await this.configureRefs();
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.start_opened_date) {
        this.filters.start_opened_date = query.start_opened_date;
      }

      if (query.end_opened_date) {
        this.filters.end_opened_date = query.end_opened_date;
      }

      if (query.start_closed_date) {
        this.filters.start_closed_date = query.start_closed_date;
      }

      if (query.end_closed_date) {
        this.filters.end_closed_date = query.end_closed_date;
      }

      if (query.locations) {
        const decodeLoc = decodeURIComponent(query.locations);
        this.filters.locations = decodeLoc.split(",");
      }

      if (query.users) {
        const decodeLoc = decodeURIComponent(query.users);
        this.filters.users = decodeLoc.split(",");
      }

      if (query.assigned != null) {
        const decodeLoc = decodeURIComponent(query.assigned);
        this.filters.assigned = decodeLoc.split(",");
      }

      if (query.vacancies != null) {
        const decodeLoc = decodeURIComponent(query.vacancies);
        this.filters.vacancies = decodeLoc.split(",");
      }

      if (query.tags != null) {
        const decodeLoc = decodeURIComponent(query.tags);
        this.filters.tags = decodeLoc.split(",");
      }

      if (query.status) {
        this.filters.status = query.status;
      }

      if (query.urgency !== undefined) {
        this.filters.urgency = query.urgency;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.usersList = (await this.getUsersSearch({})).data.items;

    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [this.serviceStartField, ...this.visibleFields, this.serviceEndField];
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.start_opened_date || this.filters.end_opened_date) {
        tags.push({ key: 'opened_date', label: 'Дата открытия' });
      }

      if (this.filters.start_closed_date || this.filters.end_closed_date) {
        tags.push({ key: 'closed_date', label: 'Дата закрытия' });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Участники: ${this.filters.users.length}`,});
      }

      if (this.filters.status !== defaults.status) {
        const foundItem = this.statusOptions.find(s => s.value === this.filters.status);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус заявки' });
      }

      if (this.filters.urgency !== defaults.urgency) {
        tags.push({ key: 'urgency', label: `Срочно: ${this.filters.urgency ? 'Да' : 'Нет'}`});
      }

      if (this.filters.assigned.length > 0) {
        tags.push({ key: 'assigned', label: `Ответственные: ${this.filters.assigned.length}`,});
      }

      if (this.filters.vacancies.length > 0) {
        tags.push({ key: 'vacancy', label: `Вакансии: ${this.filters.vacancies.length}`,});
      }

      if (this.filters.tags.length > 0) {
        tags.push({ key: 'tag', label: `Теги: ${this.filters.tags.length}`,});
      }

      return tags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.locationId ? [this.locationId] : this.filters.locations,
        users: this.filters.users,
        status: this.filters.status,
        urgency: this.filters.urgency,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        tags: this.filters.tags,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getRecruitTasks(data);
      this.isBusy = false;

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;

      this.items.forEach(item => {
        let variants = item._cellVariants || {};
        if (item.urgency === true) {
          variants.urgency = "danger";
        }
        this.$set(item, '_cellVariants', variants);
      });

      this.updateSelected();
      this.$emit('refresh');
    },
    async getAllRecruitTasks() {
      const data = {
        page: 1,
        size: this.totalRows,
        start_opened_date: this.filters.start_opened_date,
        end_opened_date: this.filters.end_opened_date,
        start_closed_date: this.filters.start_closed_date,
        end_closed_date: this.filters.end_closed_date,
        locations: this.filters.locations,
        users: this.filters.users,
        status: this.filters.status,
        urgency: this.filters.urgency,
        assigned: this.filters.assigned,
        vacancies: this.filters.vacancies,
        tags: this.filters.tags,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      const result = await this.getRecruitTasks(data);

      if (result.data && result.data.items.length > 0) {
        return result.data.items;
      }

      return [];
    },
    showModal(recruitTaskId, isViewonly) {
      const canEdit = this.canEditRecruitTasks();

      if (this.isIntegrated) {
        this.$refs.recruit_task.showModal(recruitTaskId || null, isViewonly && !canEdit);
        return;
      }

      if (!recruitTaskId) {
        if (!canEdit) return;

        this.$refs.recruit_task.showModal(null, false);
        this.onPopoverClose();
        return;
      }

      const isView = isViewonly || !canEdit;
      const query = isView ? { mode: 'view' } : {};
      this.onPopoverClose();

      this.$router.push({
        name: 'RecruitTasks',
        params: { id: recruitTaskId },
        query: query
      });
    },
    onRowDblClicked(item) {
      this.showModal(item.id, false);
    },
    onModalClosed() {
      if (this.isIntegrated) return;

      if (this.$route.params.id) {
        this.$router.replace({
          name: 'RecruitTasks',
          params: { id: null },
          hash: this.$route.hash,
        }).catch(()=>{});
      }
    },
    getAssignedName(userId) {
      if (!userId) return null;

      const user = this.usersList.find(u => u.id === userId);
      if (!user) { return '' }
      return user.name;
    },
    getStatusText(statusValue) {
      const option = this.statusOptions.find(opt => opt.value === statusValue);
      return option.text;
    },
    onRemove(row) {
      if (!this.canEditRecruitTasks()) return;

      if (row.item) {
        this.deleteRow = row.item
      }
      const title = "Удаление заявки";
      const message = `Вы действительно хотите удалить заявку "${this.deleteRow.location_vacancy_name}"?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteRecruitTask(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заявка удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        start_opened_date: null,
        end_opened_date: null,
        start_closed_date: null,
        end_closed_date: null,
        locations: [],
        users: [],
        status: null,
        urgency: null,
        assigned: [],
        vacancies: [],
        tags: []
      };
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      await this.configureRefs();

      await this.onSearch();
    },
    removeFilterTag(tag) {
      if (tag.key === 'opened_date') {
        this.filters.start_opened_date = null;
        this.filters.end_opened_date = null;
      }
      else if (tag.key === 'closed_date') {
        this.filters.start_closed_date = null;
        this.filters.end_closed_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.locations = [];
        this.$refs.user_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status = null;
      }
      else if (tag.key === 'urgency') {
        this.filters.urgency = null;
      }
      else if (tag.key === 'assigned') {
        this.filters.assigned = [];
        this.$refs.user_multi_select_assigned.onClearSelected();
      }
      else if (tag.key === 'vacancy') {
        this.filters.vacancies = [];
        this.$refs.vacancy_multi_select.onClearSelected();
      }
      else if (tag.key === 'tag') {
        this.filters.tags = [];
        this.$refs.tag_multi_select.onClearSelected();
      }

      this.onSearch();
    },
    async configureRefs() {
      if (this.$refs.user_multi_select != null) {
        await this.$refs.user_multi_select.configure(this.filters.users);
      }

      if (this.$refs.user_multi_select_assigned != null) {
        await this.$refs.user_multi_select_assigned.configure(this.filters.assigned);
      }

      if (this.$refs.location_multi_select != null) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.vacancy_multi_select != null) {
        await this.$refs.vacancy_multi_select.configure(this.filters.vacancies);
      }
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(item => { return item.id; });
    },
    onSelectUser(users) {
      this.filters.users = users.map(item => { return item.id; });
    },
    onSelectUserAssigned(users) {
      this.filters.assigned = users.map(item => { return item.id; });
    },
    onSelectVacancy(vacancies) {
      this.filters.vacancies = vacancies.map(item => { return item.id; });
    },
    onSelectTag(tags) {
      this.filters.tags = tags.map(item => { return item.id; });
    },
    // Kanban
    toggleView() {
      this.viewMode = this.viewMode === 'table' ? 'kanban' : 'table';
      this.selected = [];
      this.updateSelected();
    },
    async kanbanUpdateStatus({ item, newStatus, oldStatus }) {
      const recruitTask = this.items.find(t => t.id === item.id);
      recruitTask.status = newStatus;

      const result = await this.putRecruitTask(recruitTask);

      if (result.error || !result.data) {
        recruitTask.status = oldStatus;
        this.showError('Не удалось обновить статус.');
        await this.getItems();
      } else {
        this.showToast('Статус заявки обновлён.', 'Успех', 'success');
      }
    },
    // Update status
    async selectAllRows(checked) {
      if (checked) {
        if (this.totalRows > 10000) {
          this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
          this.allSelected = false;
          return;
        }

        this.selected = await this.getAllRecruitTasks();
        this.updateSelected();
      } else {
        this.selected = [];
        this.updateSelected();
      }
    },
    rowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
        this.$set(item, 'selected', false);
      } else {
        this.selected.push(item);
        this.$set(item, 'selected', true);
      }
    },
    updateSelected() {
      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    onShown() {
      if (this.$refs.allTasksStatusSelect) {
        this.focusRef(this.$refs.allTasksStatusSelect);
      }
    },
    onHidden() {
      if (this.$refs.allTasksStatusSelect) {
        this.focusRef(this.$refs.allTasksStatusSelect);
      }
    },
    focusRef(ref) {
      this.$nextTick(() => {
        this.$nextTick(() => {
          (ref.$el || ref).focus();
        })
      })
    },
    onPopoverClose() {
      this.selectedNewStatus = null;
      this.popoverShow = false;
    },
    async onPopoverOk() {
      const recruitTasksIds = this.selected.map(item => item.id);

      const data = {
        recruit_task_ids: recruitTasksIds,
        status_id: this.selectedNewStatus
      };

      this.isBusy = true;
      const result = await this.putRecruitTaskUpdateStatuses(data);
      this.isBusy = false;

      if (result.error) {
        this.showError(result.error.message);
      } else {
        this.showToast(`Статус успешно обновлен для ${recruitTasksIds.length} заявок.`, 'Успех', 'success');
        this.selected = [];
        this.allSelected = false;
        this.onPopoverClose();
        await this.getItems();
      }
    },
    handleJobPosting(recruitTaskId) {
      if (!this.canEditRecruitJobPostings()) return;

      this.$router.push({
        name: 'RecruitJobPostings',
        params: {
          recruitTaskId: recruitTaskId,
          recruitTaskDisabled: true
        }
      });
    },
    // Permissions
    canEditRecruitTasks() {
      return this.hasPermission([PERM.RECRUIT_TASK_EDIT]);
    },
    canEditRecruitJobPostings() {
      return this.hasPermission([PERM.RECRUIT_JOB_POSTING_EDIT]);
    }
  },
  watch: {
    '$route.params.id': {
      handler(newId) {
        this.$nextTick(() => {
          if (newId && this.$refs.recruit_task) {
            const isViewonly = this.$route.query.mode === 'view';
            this.$refs.recruit_task.showModal(newId, isViewonly);
          } else if (!newId && this.$refs.recruit_task) {
            this.$refs.recruit_task.hideModal();
          }
        });
      }, immediate: true
    },
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.totalRows) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
    totalRows(newValue) {
      if (newValue === 0) {
        this.allSelectDisabled = true;
      } else this.allSelectDisabled = newValue > 10000;
    },
  }
}
</script>

<style>
.recruitTasksId {
  padding-left: 10px !important;
  width: 60px;
  text-align: center;
}

.recruitTasksAction {
  width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

#recruit-tasks td {
  vertical-align: middle;
}

.recruitTasksSelect {
  min-width: 50px;
  text-align: center;
}

.close {
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kb-card:hover {
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-3px);
  z-index: 2;
  cursor: grab;
}

.ghost-card .kb-card {
  transform: translateY(0) !important;
  box-shadow: none !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Recruiting/RecruitTasks.vue